// src/frontend_react/src/pages/AdminDashboard/RoleManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Table from '../../components/ui/Table/Table';
import Input from '../../components/ui/Input/Input';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Modal from '../../components/ui/Modal/Modal';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';

import { notify } from '../../services/notificationService';

// Import the SHARED Admin CSS module
import adminPageStyles from './styles.module.css';

// API service
import { getAdminRoles, createAdminRole, updateAdminRole, deleteAdminRole } from '../../services/api';

// Icons
import { FiShield, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';

function RoleManagementPage() {
  const [confirmationInput, setConfirmationInput] = useState('');

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formError, setFormError] = useState(null);

  const [error, setError] = useState(null);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [currentRole, setCurrentRole] = useState({ id: null, name: '', description: '' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true); 
    setError(null); // --- FIX 1: Also reset the error state on fetch ---
    try {
      const data = await getAdminRoles();
      setRoles(Array.isArray(data) ? data.sort((a, b) => a.name.localeCompare(b.name)) : []);
    } catch (err) {
      setError(err.message || "Failed to fetch roles.");
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleCreateNew = useCallback(() => {
    setIsEditMode(false);
    setCurrentRole({ id: null, name: '', description: '' });
    setFormError(null);
    // --- FIX 2: Removed reference to non-existent 'setOperationMessage' ---
    setIsFormVisible(true);
  }, []);

  const handleEdit = useCallback((role) => {
    setIsEditMode(true);
    setCurrentRole({ ...role });
    setFormError(null);
    // --- FIX 2: Removed reference to non-existent 'setOperationMessage' ---
    setIsFormVisible(true);
  }, []);

  const openDeleteConfirm = useCallback((role) => {
    setRoleToDelete(role);
    setConfirmationInput('');
    setShowDeleteConfirm(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setRoleToDelete(null);
    setShowDeleteConfirm(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!roleToDelete) return;
    setIsSubmitting(true);
    // setError(null) is not needed here as toasts handle action errors
    try {
      await deleteAdminRole(roleToDelete.id);
      notify.success(`Role '${roleToDelete.name}' deleted successfully.`);
      fetchRoles();
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || `Failed to delete role '${roleToDelete.name}'.`); 
    } finally {
      setIsSubmitting(false);
      closeDeleteConfirm();
    }
  }, [roleToDelete, fetchRoles, closeDeleteConfirm]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentRole(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!currentRole.name.trim()) {
      setFormError("Role name is required.");
      return;
    }
    setFormError(null); 
    setIsSubmitting(true); 
    try {
      const payload = {
        name: currentRole.name.trim(),
        description: currentRole.description.trim(),
      };
      if (isEditMode && currentRole.id) {
        await updateAdminRole(currentRole.id, payload);
      } else {
        await createAdminRole(payload);
      }
      notify.success(`Role '${payload.name}' ${isEditMode ? 'updated' : 'created'} successfully.`); 
      setIsFormVisible(false);
      fetchRoles();
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || "Failed to save role.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = React.useMemo(() => [
    { Header: 'ID', accessor: 'id', width: 80 },
    { Header: 'Role Name', accessor: 'name' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ value }) => value
        ? <Typography variant="body2" color="secondary">{value}</Typography>
        : <Typography variant="caption" color="placeholder">N/A</Typography>
    },
    {
      Header: 'Actions',
      id: 'actions',
      width: 220,
      Cell: ({ row }) => {
        if (!row) {
            return <Typography variant="caption" color="placeholder">Actions N/A</Typography>;
        }
        const isProtectedRole = row.name === 'system_admin' ||
                                (typeof row.name === 'string' && row.name.startsWith('data_steward:'));
        return (
            <HStack spacing="sm">
            <Button
                variant="outline" size="sm" onClick={() => handleEdit(row)}
                leftIcon={<Icon as={FiEdit} />} aria-label={`Edit role ${row.name}`}
                disabled={isSubmitting || isLoading}
            >
                Edit
            </Button>
            <Button
                variant="destructive" size="sm" onClick={() => openDeleteConfirm(row)}
                leftIcon={<Icon as={FiTrash2} />} aria-label={`Delete role ${row.name}`}
                disabled={isSubmitting || isLoading || isProtectedRole}
                title={isProtectedRole ? "This system role cannot be deleted." : `Delete role ${row.name}`}
            >
                Delete
            </Button>
            </HStack>
        );
      }
    },
  ], [isSubmitting, isLoading, handleEdit, openDeleteConfirm]);

  if (isLoading && roles.length === 0 && !isFormVisible) {
    return (
      <Card title="Role Management" titleIcon={<Icon as={FiShield} />}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py="xl">
          <Spinner size="lg" />
          <Typography mt="md" color="secondary">Loading roles...</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      title="Role Management"
      titleIcon={<Icon as={FiShield} />}
      headerActions={!isFormVisible && (
        <Button
          variant="primary" onClick={handleCreateNew}
          leftIcon={<Icon as={FiPlus} />} disabled={isSubmitting || isLoading}
        >
          Create New Role
        </Button>
      )}
    >
      <Stack spacing="lg">
        <Typography variant="body1" color="secondary">
          Manage user roles and their descriptions. Roles define user permissions across the platform.
        </Typography>
        {/* --- FIX 1: Restore the page-level error Alert --- */}
        {error && <Alert variant="error" title="Page Load Error" onDismiss={() => setError(null)}>{error}</Alert>}
        
        {isFormVisible ? (
          <Stack spacing="lg" className={adminPageStyles.formSectionWithinCardBody} >
            <form onSubmit={handleFormSubmit}>
              <Stack spacing="lg">
                <Typography variant="h4" component="h3" gutterBottom>
                  {isEditMode ? `Edit Role: ${currentRole.name}` : "Create New Role"}
                </Typography>
                {formError && <Alert variant="error" title="Form Error" onDismiss={() => setFormError(null)}>{formError}</Alert>}
                <Input
                  label="Role Name" id="roleName" name="name" value={currentRole.name}
                  onChange={handleFormChange} required disabled={isSubmitting} maxLength={80}
                  hintText="Unique system name for the role (e.g., data_analyst)."
                />
                <Input
                  label="Description" id="roleDescription" name="description" value={currentRole.description}
                  onChange={handleFormChange} disabled={isSubmitting} maxLength={255}
                  isTextarea rows={3}
                  hintText="A brief explanation of what this role permits."
                />
                <HStack spacing="md" pt="md" className={adminPageStyles.formActionRow} >
                  <Button type="submit" variant="primary" isLoading={isSubmitting} disabled={isSubmitting}>
                    {isEditMode ? "Save Changes" : "Create Role"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsFormVisible(false)} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </HStack>
              </Stack>
            </form>
          </Stack>
        ) : (
          isLoading && roles.length > 0 ? (
             <Box display="flex" justifyContent="center" py="xl"><Spinner size="lg"/></Box>
          ) : roles.length === 0 && !isLoading && !error ? (
            <Alert variant="info">No roles found. Click "Create New Role" to add one.</Alert>
          ) : roles.length > 0 ? (
            <Table columns={columns} data={roles} isLoading={isLoading} />
          ) : null
        )}
        {showDeleteConfirm && roleToDelete && (

          <Modal
            title={`Confirm Deletion: ${roleToDelete.name}`}
            isOpen={showDeleteConfirm}
            onClose={closeDeleteConfirm}
            size="md"
            footer={
              <HStack spacing="sm" justifyContent="flex-end">
                <Button variant="outline" onClick={closeDeleteConfirm} disabled={isSubmitting}>Cancel</Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDelete} 
                  isLoading={isSubmitting} 
                  disabled={isSubmitting || confirmationInput !== roleToDelete.name}
                >
                  Delete Role
                </Button>
              </HStack>
            }
          >
            <Stack spacing="md">
              <Typography>Are you sure you want to delete the role "<strong>{roleToDelete.name}</strong>"?</Typography>
              <Alert variant="warning" title="This action cannot be undone.">
                The backend will prevent deletion if this role is assigned to any users. If it is unused, it will be permanently deleted.
              </Alert>
              <Input
                label={<span>Please type the role name <strong>{roleToDelete.name}</strong> to confirm.</span>}
                id="delete-role-confirm-input"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                autoFocus
                disabled={isSubmitting}
              />
            </Stack>
          </Modal>


        )}
      </Stack>
    </Card>
  );
}
export default RoleManagementPage;